import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RightOutlined, FolderOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import styles from './Breadcrumb.module.scss';
import { useSecureService } from '../../store/service';

const MAX_VISIBLE = 4;

interface BreadcrumbProps {
  pathParts?: string[];
  location?: Location;
  currentEntry?: any;
  /** Player / file detail üst şerit: dar alanda ellipsis, yatay scroll yok */
  className?: string;
}

interface BreadcrumbItem {
  name: string;
  path: string;
  displayName: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getBreadcrumb } = useSecureService();
  const isExternalProjectsPage = location.pathname.startsWith('/external-projects');
  const isExternalEntryPath = location.pathname.startsWith('/ext/');
  const isExternalTheme = isExternalProjectsPage || isExternalEntryPath;
  const rootBreadcrumbItem = useMemo(
    () =>
      isExternalTheme
        ? { name: "External Projects", path: "/external-projects", displayName: "External Projects" }
        : { name: "Internal Projects", path: "/internal-projects", displayName: "Internal Projects" },
    [isExternalTheme]
  );
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[]>([
    rootBreadcrumbItem
  ]);
  const [loading, setLoading] = useState(true);
  const lastFetchedPath = useRef<string | null>(null);

  useEffect(() => {
    // URL'den path'i al ve # veya ? sonrasını temizle
    // örn: /ext/b6f1b969-9f5c-4771-a935-527a9472ab60#v1 -> ext/b6f1b969-9f5c-4771-a935-527a9472ab60
    const rawPath = location.pathname.replace(/^\//, '');
    const urlPath = rawPath.split('#')[0].split('?')[0].trim();
    
    // root proje sayfalarındaysak sadece ilgili root breadcrumb'u göster
    const firstSegment = urlPath.split('/')[0];
    if (!urlPath || firstSegment === 'projects' || firstSegment === 'internal-projects' || firstSegment === 'external-projects' || firstSegment === 'dashboard' || firstSegment === 'settings') {
      setBreadcrumbItems([rootBreadcrumbItem]);
      setLoading(false);
      return;
    }
    
    // Aynı path için tekrar fetch yapma
    if (lastFetchedPath.current === urlPath) {
      return;
    }

    const fetchBreadcrumbData = async () => {
      setLoading(true);

      try {
        const response = await getBreadcrumb(urlPath);
        const rawItems = response && 'data' in response ? response.data?.items : undefined;
        if (response.success && Array.isArray(rawItems)) {
          const items: BreadcrumbItem[] = [
            rootBreadcrumbItem,
            ...rawItems.map((item: any) => ({
              name: item.name || '',
              path: item.url ? `/${item.url}` : rootBreadcrumbItem.path,
              displayName: item.name || ''
            }))
          ];
          
          setBreadcrumbItems(items);
          lastFetchedPath.current = urlPath;
        }
      } catch (error) {
        console.error('Error fetching breadcrumb data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBreadcrumbData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, rootBreadcrumbItem]);

  if (loading) {
    return (
      <div
        className={`${styles.breadcrumbContainer}${className ? ` ${className}` : ''}`}
      >
        <span className={styles.breadcrumbItem}>Loading...</span>
      </div>
    );
  }

  const showEllipsis = breadcrumbItems.length > MAX_VISIBLE;
  const firstItem = breadcrumbItems[0];
  const lastTwo = showEllipsis ? breadcrumbItems.slice(-2) : [];
  const middleItems = showEllipsis ? breadcrumbItems.slice(1, -2) : [];
  const lastIndex = breadcrumbItems.length - 1;

  const renderSegment = (item: BreadcrumbItem, index: number, isActive: boolean) => (
    <div key={`${item.path}-${index}`} className={styles.breadcrumbItemWrapper}>
      <span
        onClick={() => !isActive && navigate(item.path)}
        className={`${styles.breadcrumbItem} ${isActive ? styles.active : ''}`}
      >
        {item.displayName}
      </span>
    </div>
  );

  return (
    <div
      className={`${styles.breadcrumbContainer}${isExternalTheme ? ` ${styles.externalTheme}` : ''}${
        className ? ` ${className}` : ''
      }`}
    >
      {!showEllipsis ? (
        breadcrumbItems.map((item, index) => (
          <React.Fragment key={item.path}>
            {index > 0 && <RightOutlined className={styles.breadcrumbSeparator} />}
            {renderSegment(item, index, index === lastIndex)}
          </React.Fragment>
        ))
      ) : (
        <>
          <div className={styles.breadcrumbItemWrapper}>
            <span onClick={() => navigate(firstItem.path)} className={styles.breadcrumbItem}>
              {firstItem.displayName}
            </span>
          </div>
          <RightOutlined className={styles.breadcrumbSeparator} />
          <div className={styles.breadcrumbItemWrapper}>
            <Popover
              arrow={false}
              trigger="click"
              placement="bottomLeft"
              overlayClassName={styles.breadcrumbPopover}
              content={
                <div className={styles.breadcrumbPopoverContent}>
                  {middleItems.map((mid, idx) => (
                    <button
                      key={`${mid.path}-${idx}`}
                      type="button"
                      className={styles.breadcrumbPopoverItem}
                      onClick={() => navigate(mid.path)}
                    >
                      <FolderOutlined className={styles.breadcrumbPopoverIcon} />
                      <span className={styles.breadcrumbPopoverLabel}>{mid.displayName}</span>
                    </button>
                  ))}
                </div>
              }
            >
              <span className={styles.breadcrumbItem}>...</span>
            </Popover>
          </div>
          {lastTwo.map((item, i) => (
            <React.Fragment key={`${item.path}-${i}`}>
              <RightOutlined className={styles.breadcrumbSeparator} />
              {renderSegment(item, lastTwo.length - 2 + i, i === lastTwo.length - 1)}
            </React.Fragment>
          ))}
        </>
      )}
    </div>
  );
};

export default Breadcrumb;
